import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | InvizHer - Empowering Developers & Tech Enthusiasts</title>
        <meta name="description" content="Learn about InvizHer - your trusted source for programming tutorials, tech insights, and development resources. Discover our mission to empower developers worldwide." />
        <meta name="keywords" content="about InvizHer, programming blog, tech education, coding tutorials, developer community, tech resources" />
        <link rel="canonical" href="https://invizher.com/about" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://invizher.com/about" />
        <meta property="og:title" content="About Us | InvizHer" />
        <meta property="og:description" content="Learn about InvizHer - empowering developers through quality programming tutorials and tech content." />
        <meta property="og:site_name" content="InvizHer" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About InvizHer" />
        <meta name="twitter:description" content="Empowering developers through quality programming tutorials." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-24 md:pt-28" role="main">
          {/* Hero Section */}
          <section className="container mx-auto px-4 sm:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center animate-fade-in">
              <div className="inline-block mb-4">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center">
                  <i className="fas fa-users text-3xl md:text-4xl gradient-text"></i>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 px-4">
                About <span className="gradient-text">InvizHer</span>
              </h1>
              <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8 leading-relaxed px-4">Empowering minds through technology, design, and innovative content</p>
              <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm md:text-base">
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                  <i className="fas fa-check-circle text-blue-500"></i>
                  <span className="text-gray-700">Quality Content</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
                  <i className="fas fa-check-circle text-purple-500"></i>
                  <span className="text-gray-700">Expert Insights</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-pink-50 rounded-full">
                  <i className="fas fa-check-circle text-pink-500"></i>
                  <span className="text-gray-700">Community Driven</span>
                </div>
              </div>
            </div>
          </section>

          {/* Author Profile Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12 gradient-text">Meet the Founder</h2>
              <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 shadow-xl">
                <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                  <div className="relative order-2 md:order-1">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl md:rounded-3xl blur-2xl"></div>
                    <img 
                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcST3E33sS_h3n-VYraK7129h9s1UYjBFDof2w&s" 
                      alt="Pavan Magar - Founder" 
                      className="relative rounded-2xl md:rounded-3xl w-full h-64 md:h-auto object-cover shadow-xl"
                    />
                  </div>
                  <div className="space-y-4 md:space-y-6 order-1 md:order-2">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold gradient-text mb-2">Pavan Magar</h3>
                      <p className="text-gray-600 text-sm md:text-base">Founder & Lead Content Creator</p>
                    </div>
                    <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                      With over a decade of experience in technology and design, I'm passionate about sharing knowledge and inspiring others to embrace innovation.
                    </p>
                    <div className="pt-4">
                      <p className="text-sm font-semibold text-gray-600 mb-3">Connect with me:</p>
                      <div className="flex gap-3 md:gap-4">
                        <a 
                          href="https://youtube.com/@invizher" 
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-all hover:scale-105"
                          title="YouTube"
                        >
                          <i className="fab fa-youtube text-lg md:text-xl"></i>
                          <span className="text-sm hidden sm:inline">YouTube</span>
                        </a>
                        <a 
                          href="https://t.me/invizher" 
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all hover:scale-105"
                          title="Telegram"
                        >
                          <i className="fab fa-telegram text-lg md:text-xl"></i>
                          <span className="text-sm hidden sm:inline">Telegram</span>
                        </a>
                        <a 
                          href="https://instagram.com/invizher" 
                          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-50 text-pink-600 hover:bg-pink-100 transition-all hover:scale-105"
                          title="Instagram"
                        >
                          <i className="fab fa-instagram text-lg md:text-xl"></i>
                          <span className="text-sm hidden sm:inline">Instagram</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Mission & Vision Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12 gradient-text">Our Mission & Vision</h2>
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-xl transition-all group">
                  <div className="w-14 h-14 md:w-16 md:h-16 mb-4 md:mb-6 rounded-xl md:rounded-2xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-lightbulb text-blue-500 text-xl md:text-2xl"></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Our Mission</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">To provide accessible, high-quality content that bridges the gap between technology and everyday life, empowering individuals to embrace digital innovation.</p>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-blue-500 mt-1"></i>
                        <span>Quality educational content</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-blue-500 mt-1"></i>
                        <span>Accessible to everyone</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-blue-500 mt-1"></i>
                        <span>Practical real-world applications</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-xl transition-all group">
                  <div className="w-14 h-14 md:w-16 md:h-16 mb-4 md:mb-6 rounded-xl md:rounded-2xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-eye text-purple-500 text-xl md:text-2xl"></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Our Vision</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">To create a community where knowledge sharing drives innovation and personal growth, making technology and design accessible to everyone.</p>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-purple-500 mt-1"></i>
                        <span>Thriving learning community</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-purple-500 mt-1"></i>
                        <span>Innovation through collaboration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fas fa-check text-purple-500 mt-1"></i>
                        <span>Global impact and reach</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* What We Do Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center gradient-text">What We Do</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-xl transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 mb-4 md:mb-6 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-pen-fancy text-blue-500 text-lg md:text-xl"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Content Creation</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">Producing engaging articles, tutorials, and resources on technology, design, and digital innovation.</p>
                  <div className="flex items-center text-sm text-blue-500 font-medium">
                    <span>Learn more</span>
                    <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
                <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-xl transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 mb-4 md:mb-6 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-users text-purple-500 text-lg md:text-xl"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Community Building</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">Creating spaces for knowledge sharing and networking among technology enthusiasts.</p>
                  <div className="flex items-center text-sm text-purple-500 font-medium">
                    <span>Join us</span>
                    <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
                <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 hover:shadow-xl transition-all group sm:col-span-2 lg:col-span-1">
                  <div className="w-12 h-12 md:w-14 md:h-14 mb-4 md:mb-6 rounded-xl bg-pink-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-graduation-cap text-pink-500 text-lg md:text-xl"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Education</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">Providing educational resources and tutorials to help you stay ahead in the digital world.</p>
                  <div className="flex items-center text-sm text-pink-500 font-medium">
                    <span>Explore</span>
                    <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
            <div className="max-w-6xl mx-auto">
              <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-12">
                <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12 gradient-text">Our Impact</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                  <div className="text-center p-4">
                    <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">10K+</div>
                    <p className="text-gray-600 text-sm md:text-base">Readers</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">500+</div>
                    <p className="text-gray-600 text-sm md:text-base">Articles</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">50+</div>
                    <p className="text-gray-600 text-sm md:text-base">Categories</p>
                  </div>
                  <div className="text-center p-4 col-span-2 lg:col-span-1">
                    <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">24/7</div>
                    <p className="text-gray-600 text-sm md:text-base">Available</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16 pb-16 md:pb-20">
            <div className="max-w-6xl mx-auto glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12 text-center gradient-text">Important Links</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
                <Link to="/privacy" className="flex flex-col items-center space-y-2 md:space-y-3 p-4 rounded-xl hover:bg-gray-50/50 transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-shield-alt text-blue-500 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-gray-700 group-hover:text-blue-500 transition-colors text-center text-sm md:text-base font-medium">Privacy Policy</span>
                </Link>
                <Link to="/faq" className="flex flex-col items-center space-y-2 md:space-y-3 p-4 rounded-xl hover:bg-gray-50/50 transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-question-circle text-purple-500 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-gray-700 group-hover:text-purple-500 transition-colors text-center text-sm md:text-base font-medium">FAQ</span>
                </Link>
                <Link to="/disclaimer" className="flex flex-col items-center space-y-2 md:space-y-3 p-4 rounded-xl hover:bg-gray-50/50 transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-pink-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-info-circle text-pink-500 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-gray-700 group-hover:text-pink-500 transition-colors text-center text-sm md:text-base font-medium">Disclaimer</span>
                </Link>
                <Link to="/contact" className="flex flex-col items-center space-y-2 md:space-y-3 p-4 rounded-xl hover:bg-gray-50/50 transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="fas fa-envelope text-green-500 text-lg md:text-xl"></i>
                  </div>
                  <span className="text-gray-700 group-hover:text-green-500 transition-colors text-center text-sm md:text-base font-medium">Contact Us</span>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;
